import React, { useState, useEffect } from "react";

export default function ReviewsSlider() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fallback initial data in case CMS is slow
  const fallbackReviews = [
    {
      id: "1",
      author: "Haftor",
      location: "",
      rating: 5,
      text: "Jeg har samarbejdet med ETDanmark flere gange i løbet af de sidste tre måneder og har haft en rigtig god oplevelse hver gang.",
      avatar: [],
      source: "Google Anmeldelse",
      date: "7 måneder siden",
    },
    {
      id: "2",
      author: "Søren",
      location: "",
      rating: 5,
      text: "God og ordentlig service til attraktive priser, samt en venlig kommunikation.",
      avatar: [],
      source: "Google Anmeldelse",
      date: "1 måned siden",
    },
    {
      id: "3",
      author: "Trustpilot-anmelder",
      location: "",
      rating: 5,
      text: "Lars leverede en ordentligt og professionel service til en særdeles attraktiv pris, samt en god kommunikation før og under arbejdet.",
      avatar: [],
      source: "Trustpilot",
      date: "1 måned siden",
    },
    {
      id: "4",
      author: "Trustpilot-anmelder",
      location: "",
      rating: 5,
      text: "Super service meget professionel. Ingen ventetid.",
      avatar: [],
      source: "Trustpilot",
      date: "3 uger siden",
    },
    {
      id: "5",
      author: "Trustpilot-anmelder",
      location: "",
      rating: 5,
      text: "Vi havde en god oplevelse med etdanmark. Lars var professionel, struktureret, og nem at kommunikere med, alle aftaler overholdt til tiden, og en god pris. Klar anbefaling!",
      avatar: [],
      source: "Trustpilot",
      date: "7 måneder siden",
    },
  ];

  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/content/reviews");
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setReviews(data);
          } else {
            setReviews(fallbackReviews);
          }
        } else {
          setReviews(fallbackReviews);
        }
      } catch (err) {
        console.error("Error fetching reviews:", err);
        setReviews(fallbackReviews);
      } finally {
        setLoading(false);
      }
    }
    fetchReviews();
  }, []);

  const displayList = reviews.length > 0 ? reviews : fallbackReviews;
  // Duplicate for seamless infinite marquee loop
  const marqueeItems = [...displayList, ...displayList];

  return (
    <section
      id="anmeldelser"
      className="py-16 sm:py-20 bg-stone-50 border-y border-stone-200/60 overflow-hidden scroll-mt-8 sm:scroll-mt-12"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-10 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
          Se hvad vores kunder siger om os
        </h2>
        <p className="text-stone-600 max-w-2xl mx-auto mt-2 text-sm sm:text-base">
          Vi lægger stor vægt på faglighed, præcision og tæt dialog. Her er tilbagemeldinger fra boligejere og bygherrer i Jylland og Fyn.
        </p>

        {/* Rating summary badges */}
        <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
          <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white border border-stone-200 shadow-sm text-xs font-semibold text-stone-800">
            <span className="text-amber-400 text-sm">★★★★★</span>
            <span>5.0 / 5.0 på Google</span>
          </div>
          <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white border border-stone-200 shadow-sm text-xs font-semibold text-stone-800">
            <span className="w-4 h-4 rounded-sm bg-[#00b67a] text-white flex items-center justify-center text-[10px]">★</span>
            <span>Trustpilot verificeret</span>
          </div>
        </div>
      </div>

      {/* Infinite Horizontal Carousel Container with Pause-on-hover */}
      <div className="relative w-full overflow-hidden [mask-image:_linear-gradient(to_right,transparent_0,_black_100px,_black_calc(100%-100px),transparent_100%)]">
        <div
          className="animate-infinite-scroll flex gap-6 py-4"
          style={{
            animationDuration: `${Math.max(30, displayList.length * 8.4)}s`,
          }}
        >
          {marqueeItems.map((review, index) => {
            const avatarUrl =
              Array.isArray(review.avatar) && review.avatar.length > 0
                ? review.avatar[0]
                : typeof review.avatar === "string"
                ? review.avatar
                : null;

            return (
              <div
                key={`${review.id || index}-${index}`}
                className="w-[300px] sm:w-[360px] lg:w-[390px] shrink-0 bg-white rounded-2xl p-6 border border-stone-200/80 shadow-sm hover:border-emerald-200 transition-colors flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex text-amber-400 text-xs">
                      {Array.from({ length: review.rating || 5 }).map((_, i) => (
                        <i key={i} className="fa-solid fa-star" aria-hidden="true"></i>
                      ))}
                    </div>
                    <span className="text-[11px] font-medium text-stone-400 flex items-center gap-1">
                      <i className="fa-regular fa-calendar-check" aria-hidden="true"></i>
                      {review.date || "Verificeret"}
                    </span>
                  </div>

                  <p className="text-sm text-stone-700 leading-relaxed italic">
                    "{review.text}"
                  </p>
                </div>

                <div className="mt-5 pt-4 border-t border-stone-100 flex items-center gap-3">
                  {review.source === "Trustpilot" ? (
                    <div className="w-10 h-10 rounded-full bg-[#00b67a]/15 text-[#00b67a] font-bold flex items-center justify-center shrink-0 text-sm">
                      ★
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center shrink-0 text-sm">
                      {review.author?.charAt(0) || "K"}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-stone-900 text-sm truncate">
                      {review.author}
                    </p>
                    <p className="text-xs text-stone-500 flex items-center gap-1">
                      {review.location && <span>{review.location} • </span>}
                      <span className="text-emerald-700 font-medium">
                        {review.source || "Verificeret kunde"}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
